import { Button } from '@mantine/core';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import type { DeliveryDto, IntegrationResponse, ReplayFailedResponse } from '@/api';
import { adapterName } from '@/components/Integrations/adapters/copy';
import {
  capabilitiesFor,
  useIntegrationCapabilities,
} from '@/components/Integrations/capabilities';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { Tag } from '@/components/Ui/Tag';
import { useUndoToast } from '@/components/Ui/Toast';
import { UiLink } from '@/components/Ui/UiLink';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { formatBytes } from '@/lib/messages';
import { useRustyState } from '@/state';
import shared from '../AccountScreens.module.css';
import { errorStatus, formatWhen } from '../format';
import tableClasses from './Deliveries.module.css';
import classes from './Integrations.module.css';
import {
  attemptResult,
  dispatchDetail,
  integrationTarget,
  isPayloadGone,
  isTestDelivery,
  payloadExcerpt,
  responseLine,
} from './integration-text';

const PAGE_SIZE = 50;

type ResultFilter = 'all' | 'failed' | 'delivered';

/** `failed` spans `retry` and `dlq` on the server. */
const OUTCOME_FILTER: Record<Exclude<ResultFilter, 'all'>, string> = {
  failed: 'failed',
  delivered: 'success',
};

function startOfDay(date: string) {
  return new Date(`${date}T00:00:00`).toISOString();
}

function endOfDay(date: string) {
  return new Date(`${date}T23:59:59.999`).toISOString();
}

function ChevronIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export function DeliveriesScreen() {
  const { id = '' } = useParams();
  // Keyed so another integration's page starts from fresh state.
  return <Deliveries key={id} id={id} />;
}

function Deliveries({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const toast = useUndoToast();
  const caps = useIntegrationCapabilities();
  const formsSummary = useRustyState((s) => s.formsSummary);
  const api = useRustyState.getState().api;

  const [integration, setIntegration] = useState<IntegrationResponse | null>(null);
  const [missing, setMissing] = useState(false);
  const [rows, setRows] = useState<DeliveryDto[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const generation = useRef(0);
  const [result, setResult] = useState<ResultFilter>('all');
  const [formId, setFormId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null);
  const [resendAll, setResendAll] = useState<'idle' | 'busy' | number>('idle');
  const [testing, setTesting] = useState(false);
  // Forms seen in any loaded page, so the form filter keeps its options while filtered.
  const [knownForms, setKnownForms] = useState<Record<string, string>>({});

  const when = { today: t('deliveries.today'), yesterday: t('deliveries.yesterday') };

  // Also re-read after a resend: the banner counts come from it.
  const loadIntegration = useCallback(
    () =>
      api
        .getIntegration(id)
        .then(setIntegration)
        .catch((e) => {
          if (errorStatus(e) === 404) setMissing(true);
        }),
    [api, id]
  );

  useEffect(() => {
    void loadIntegration();
    if (!useRustyState.getState().formsSummary) void useRustyState.getState().refreshFormsSummary();
  }, [loadIntegration]);

  const load = useCallback(
    async (after?: DeliveryDto) => {
      if (!after) generation.current += 1;
      const gen = generation.current;
      setLoadFailed(false);
      try {
        const res = await api.listIntegrationDeliveries(id, {
          page_size: PAGE_SIZE,
          ...(after ? { before: after.created_at, before_id: after.id } : {}),
          ...(result !== 'all' ? { outcome: OUTCOME_FILTER[result] } : {}),
          ...(formId ? { form_id: formId } : {}),
          ...(from ? { from: startOfDay(from) } : {}),
          ...(to ? { to: endOfDay(to) } : {}),
        });
        if (gen !== generation.current) return;
        const data = res.data ?? [];
        setRows((prev) => {
          if (!after || !prev) return data;
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...data.filter((r) => !seen.has(r.id))];
        });
        setHasMore(data.length === PAGE_SIZE);
        setKnownForms((prev) => {
          const next = { ...prev };
          for (const d of data) if (d.form_id) next[d.form_id] = d.form_title ?? d.form_id;
          return next;
        });
      } catch {
        if (gen !== generation.current) return;
        setLoadFailed(true);
        if (!after) setRows([]);
      }
    },
    [api, id, formId, from, to, result]
  );

  useEffect(() => {
    void load();
  }, [load]);

  if (missing) {
    return (
      <Page>
        <PageHeader
          title={t('deliveries.pageTitle')}
          breadcrumb={[{ label: t('integrations.pageTitle'), to: '/account/integrations' }]}
        />
        <p className={shared.errorText}>{t('integrations.editor.notFound')}</p>
      </Page>
    );
  }

  const kind = integration?.kind ?? '';
  // Email rows carry no HTTP exchange; the worker stores 0 for both.
  const isEmail = kind === 'email';
  const c = capabilitiesFor(caps, kind);
  // Server counts: what "resend the failed" picks up, or for adapters without resend the 7-day failures.
  const resendableCount = integration?.resendable ?? 0;
  const failedCount = c.replayable ? resendableCount : (integration?.failed_7d ?? 0);
  const lastFailure = integration?.last_failure;
  const target = integration ? integrationTarget(integration, t) : '';
  const formIdByTitle = new Map<string, string | null>();
  for (const f of formsSummary ?? []) {
    formIdByTitle.set(f.title, formIdByTitle.has(f.title) ? null : f.id);
  }

  const capabilityLine = [
    c.signed ? t('deliveries.signed') : t('deliveries.notSigned'),
    c.retryable ? t('deliveries.retried') : t('deliveries.notRetried'),
    c.replayable ? t('deliveries.canResend') : t('deliveries.cannotResend'),
  ].join(' · ');

  const resendOne = async (row: DeliveryDto) => {
    setResending(row.id);
    try {
      const res = await api.replayIntegrationDelivery(row.id, { onSuccess: false });
      toast(
        res.status === 'failed'
          ? t('deliveries.resendFailed', { detail: dispatchDetail(res) })
          : t('deliveries.resent')
      );
      await Promise.all([load(), loadIntegration()]);
    } catch {
      // the API layer reports the error
    } finally {
      setResending(null);
    }
  };

  const resendFailed = async () => {
    setResendAll('busy');
    try {
      const res: ReplayFailedResponse & { failed?: number; skipped?: number } =
        await api.replayFailedDeliveries(id);
      const failed = res.failed ?? 0;
      const skipped = res.skipped ?? 0;
      setResendAll(res.replayed + failed);
      toast(
        [
          t('deliveries.bulk.delivered', { count: res.replayed }),
          failed > 0 ? t('deliveries.bulk.failed', { count: failed }) : '',
          skipped > 0 ? t('deliveries.bulk.skipped', { count: skipped }) : '',
        ]
          .filter(Boolean)
          .join(' ')
      );
      await Promise.all([load(), loadIntegration()]);
    } catch {
      setResendAll('idle');
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await api.testIntegration(id, 'message.received', { onSuccess: false });
      toast(
        res.status === 'failed'
          ? t('deliveries.testFailed', { detail: dispatchDetail(res) })
          : t('deliveries.testSent')
      );
      await load();
    } catch {
      // the API layer reports the error
    } finally {
      setTesting(false);
    }
  };

  const resultTag = (r: DeliveryDto) => {
    const result = attemptResult(r);
    const variant =
      result === 'delivered'
        ? 'ok'
        : result === 'failed'
          ? 'risk'
          : result === 'retrying'
            ? 'pending'
            : 'neutral';
    return <Tag variant={variant}>{t(`deliveries.result.${result}`)}</Tag>;
  };

  const eventLabel = (type?: string) =>
    type === 'message.received' || !type ? t('deliveries.eventMessageReceived') : type;

  const nextAttempt = (r: DeliveryDto) => {
    if (r.next_attempt_at) return formatWhen(r.next_attempt_at, when, i18n.language);
    if (r.outcome === 'success') return t('deliveries.nextNotNeeded');
    if (r.outcome === 'dlq') return t('deliveries.nextNone');
    return '–';
  };

  const formOptions = Object.entries(knownForms);
  const headerTitle = integration
    ? t('deliveries.titleFor', { title: integration.title })
    : t('deliveries.pageTitle');

  return (
    <Page width="wide">
      <PageHeader
        title={headerTitle}
        breadcrumb={[{ label: t('integrations.pageTitle'), to: '/account/integrations' }]}
        subtitle={
          integration && (
            <span className={classes.headerFacts}>
              {target && (
                <span>
                  {kind === 'email' ? (
                    target
                  ) : (
                    <>
                      {t('deliveries.postsTo')} <span className={classes.mono}>{target}</span>
                    </>
                  )}
                </span>
              )}
              {integration.form_count !== undefined && (
                <span>
                  {integration.form_count === 0 ? (
                    t('deliveries.usedByNone')
                  ) : (
                    <>
                      {t('deliveries.usedBy')}{' '}
                      {(integration.form_titles ?? []).map((title, i) => {
                        const formRef = formIdByTitle.get(title);
                        return (
                          <Fragment key={title}>
                            {i > 0 && ', '}
                            {formRef ? (
                              <UiLink to={`/account/forms/${formRef}`}>{title}</UiLink>
                            ) : (
                              title
                            )}
                          </Fragment>
                        );
                      })}
                      {integration.form_count > (integration.form_titles ?? []).length &&
                        ` ${t('deliveries.andMore', {
                          count: integration.form_count - (integration.form_titles ?? []).length,
                        })}`}
                    </>
                  )}
                </span>
              )}
              <span>{capabilityLine}</span>
            </span>
          )
        }
        actions={
          <>
            <Button variant="default" onClick={sendTest} loading={testing}>
              {t('deliveries.sendTest')}
            </Button>
            <Button
              variant="default"
              component={Link}
              to={createLanguageURL(`/account/integrations/${id}`)}
            >
              {t('ui.edit')}
            </Button>
          </>
        }
      />

      {failedCount > 0 && (
        <section className={shared.riskBanner} aria-live="polite">
          <span className={shared.riskBannerText}>
            <strong className={shared.riskBannerTitle}>
              {t('deliveries.banner.count', { count: failedCount })}
            </strong>{' '}
            {lastFailure?.http_status
              ? t('deliveries.banner.answers', {
                  name: adapterName(kind, t),
                  status: lastFailure.http_status,
                  // The class (retry, retry-exhausted) is internal; the rows carry the detail.
                  detail: '',
                })
              : ''}{' '}
            {c.replayable ? t('deliveries.banner.fix') : t('integrations.failure.email')}
          </span>
          {c.replayable && resendableCount > 0 && (
            <Button
              size="xs"
              variant="default"
              onClick={resendFailed}
              loading={resendAll === 'busy'}
              disabled={typeof resendAll === 'number'}
            >
              {typeof resendAll === 'number'
                ? t('deliveries.banner.resentAll', { count: resendAll })
                : t('deliveries.banner.resendAll', { count: resendableCount })}
            </Button>
          )}
        </section>
      )}

      <div className={shared.filters}>
        <label htmlFor="dlv-result" className={shared.filterLabel}>
          {t('deliveries.filter.result')}
        </label>
        <select
          id="dlv-result"
          className={shared.nativeInput}
          value={result}
          onChange={(e) => setResult(e.currentTarget.value as ResultFilter)}
        >
          <option value="all">{t('deliveries.filter.all')}</option>
          <option value="failed">{t('deliveries.filter.failed')}</option>
          <option value="delivered">{t('deliveries.filter.delivered')}</option>
        </select>
        <label htmlFor="dlv-form" className={shared.filterLabel}>
          {t('deliveries.filter.form')}
        </label>
        <select
          id="dlv-form"
          className={shared.nativeInput}
          value={formId}
          onChange={(e) => setFormId(e.currentTarget.value)}
        >
          <option value="">{t('deliveries.filter.allForms')}</option>
          {formOptions.map(([fid, title]) => (
            <option key={fid} value={fid}>
              {title}
            </option>
          ))}
        </select>
        <label htmlFor="dlv-from" className={shared.filterLabel}>
          {t('deliveries.filter.from')}
        </label>
        <input
          id="dlv-from"
          type="date"
          className={shared.nativeInput}
          value={from}
          onChange={(e) => setFrom(e.currentTarget.value)}
        />
        <label htmlFor="dlv-to" className={shared.filterLabel}>
          {t('deliveries.filter.to')}
        </label>
        <input
          id="dlv-to"
          type="date"
          className={shared.nativeInput}
          value={to}
          onChange={(e) => setTo(e.currentTarget.value)}
        />
      </div>

      {loadFailed && <p className={shared.errorText}>{t('deliveries.loadFailed')}</p>}

      {rows === null ? (
        <EmptyText>{t('ui.loading')}</EmptyText>
      ) : (
        <div className={shared.tableCard}>
          <div className={tableClasses.scroll}>
            <table className={tableClasses.table}>
              <thead>
                <tr>
                  <th scope="col">
                    <span className={shared.visuallyHidden}>{t('deliveries.col.details')}</span>
                  </th>
                  <th scope="col">{t('deliveries.col.when')}</th>
                  <th scope="col">{t('deliveries.col.form')}</th>
                  <th scope="col">{t('deliveries.col.message')}</th>
                  <th scope="col" className={tableClasses.num}>
                    {t('deliveries.col.attempt')}
                  </th>
                  <th scope="col">{t('deliveries.col.result')}</th>
                  <th scope="col" className={tableClasses.num}>
                    {t('deliveries.col.http')}
                  </th>
                  <th scope="col" className={tableClasses.num}>
                    {t('deliveries.col.took')}
                  </th>
                  <th scope="col">{t('deliveries.col.error')}</th>
                  <th scope="col">
                    <span className={shared.visuallyHidden}>{t('deliveries.col.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={10} className={shared.sub}>
                      {t('deliveries.empty')}
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  const isOpen = open === r.id;
                  const payload = payloadExcerpt(r.payload_excerpt);
                  const test = isTestDelivery(r);
                  const gone = isPayloadGone(r);
                  return (
                    <Fragment key={r.id}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className={`${classes.chevron} ${isOpen ? classes.chevronOpen : ''}`}
                            aria-expanded={isOpen}
                            aria-controls={`dlv-${r.id}`}
                            aria-label={t('deliveries.showDetails')}
                            onClick={() => setOpen(isOpen ? null : r.id)}
                          >
                            <ChevronIcon />
                          </button>
                        </td>
                        <td className={shared.nowrap}>
                          {formatWhen(r.created_at, when, i18n.language)}
                        </td>
                        <td>
                          {r.form_id ? (
                            <UiLink to={`/account/forms/${r.form_id}`}>
                              {r.form_title ?? r.form_id}
                            </UiLink>
                          ) : test ? (
                            <Tag>{t('deliveries.testDelivery')}</Tag>
                          ) : gone ? (
                            <Tag>{t('deliveries.payloadGone')}</Tag>
                          ) : (
                            <span className={shared.sub}>–</span>
                          )}
                        </td>
                        <td>
                          {r.message_id ? (
                            <UiLink to={`/account/messages/${r.message_id}`}>
                              {t('deliveries.openMessage')}
                            </UiLink>
                          ) : (
                            <span className={shared.sub}>–</span>
                          )}
                        </td>
                        <td className={tableClasses.num}>{r.attempt_number}</td>
                        <td>{resultTag(r)}</td>
                        <td className={`${tableClasses.num} ${classes.mono}`}>
                          {isEmail ? '–' : (r.http_status ?? '–')}
                        </td>
                        <td className={tableClasses.num}>
                          {!isEmail && r.latency_ms !== undefined && r.latency_ms !== null
                            ? t('deliveries.ms', { ms: r.latency_ms })
                            : '–'}
                        </td>
                        <td className={classes.errorCell}>{r.error_detail || '–'}</td>
                        <td>
                          {r.can_replay && !test && (
                            <Button
                              size="xs"
                              variant="default"
                              onClick={() => resendOne(r)}
                              loading={resending === r.id}
                            >
                              {t('ui.resend')}
                            </Button>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr id={`dlv-${r.id}`} className={tableClasses.detailRow}>
                          <td />
                          <td colSpan={9}>
                            <div className={classes.details}>
                              <div className={shared.stack}>
                                <span className={classes.help}>
                                  <strong>{t('deliveries.whatWasSent')}</strong>
                                </span>
                                {payload ? (
                                  <>
                                    <pre className={classes.payload}>{payload.text}</pre>
                                    {payload.truncated && (
                                      <span className={classes.help}>
                                        {payload.size
                                          ? t('deliveries.truncatedSize', {
                                              size: formatBytes(payload.size),
                                            })
                                          : t('deliveries.truncated')}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className={shared.sub}>{t('deliveries.noPayload')}</span>
                                )}
                              </div>
                              <dl className={shared.dl}>
                                <dt>{t('deliveries.deliveryId')}</dt>
                                <dd className={classes.mono}>{r.id}</dd>
                                <dt>{t('deliveries.eventId')}</dt>
                                <dd className={classes.mono}>{r.event_id}</dd>
                                {r.idempotency_key && (
                                  <>
                                    <dt>{t('deliveries.idempotencyKey')}</dt>
                                    <dd className={classes.mono}>{r.idempotency_key}</dd>
                                  </>
                                )}
                                <dt>{t('deliveries.event')}</dt>
                                <dd>{eventLabel(r.event_type)}</dd>
                                <dt>{t('deliveries.response')}</dt>
                                <dd className={classes.mono}>{responseLine(r) || '–'}</dd>
                                {r.error_class && (
                                  <>
                                    <dt>{t('deliveries.errorClass')}</dt>
                                    <dd className={classes.mono}>{r.error_class}</dd>
                                  </>
                                )}
                                <dt>{t('deliveries.nextAttempt')}</dt>
                                <dd>{nextAttempt(r)}</dd>
                              </dl>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className={shared.footer}>
              <span className={shared.help}>{t('deliveries.loaded', { count: rows.length })}</span>
              <Button size="xs" variant="default" onClick={() => load(rows[rows.length - 1])}>
                {t('deliveries.older')}
              </Button>
            </div>
          )}
        </div>
      )}
    </Page>
  );
}
